import { useCallback, useMemo, useState } from 'react';
import { useMobilePushNotificationsDashboard } from '../../../../hooks/usePushNotifications';
import { DEFAULT_FILTERS } from '../constants/filters';

/**
 * Maneja estado de filtros, paginación y acciones de la tabla de notificaciones mobile.
 *
 * @returns {Object} Estado y handlers del listado.
 */
export const useMobileNotificationsList = () => {
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const apiFilters = useMemo(() => {
    const cleanFilters = {
      page,
      per_page: rowsPerPage,
      contract_number: appliedFilters.contractNumber?.trim() || undefined,
      subscriber_name: appliedFilters.subscriberName?.trim() || undefined,
      status: appliedFilters.status !== 'all' ? appliedFilters.status : undefined,
    };

    return cleanFilters;
  }, [
    appliedFilters.contractNumber,
    appliedFilters.status,
    appliedFilters.subscriberName,
    page,
    rowsPerPage,
  ]);

  const { data, isLoading, isFetching } = useMobilePushNotificationsDashboard(apiFilters);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
    setAppliedFilters({ ...DEFAULT_FILTERS });
    setPage(1);
  }, []);

  const handleSearch = useCallback(() => {
    setAppliedFilters((prev) => ({ ...prev, ...filters }));
    setPage(1);
  }, [filters]);

  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    const value = Number(event.target.value) || 10;
    setRowsPerPage(value);
    setPage(1);
  }, []);

  return {
    filters,
    page,
    rowsPerPage,
    notifications: data?.results || [],
    totalPages: data?.total_pages || 1,
    totalCount: data?.total || 0,
    metrics: data?.metrics || { total: 0, read: 0, unread: 0, read_ratio: 0 },
    isLoading,
    isFetching,
    handleFilterChange,
    handleResetFilters,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  };
};

export default useMobileNotificationsList;
